import { Logo } from "@/components/Logo";
import { StateMessage } from "@/components/StateMessage";
import { CashFlowChart } from "@/components/CashFlowChart";
import { CashFlowPlannerCard } from "@/components/CashFlowPlannerCard";
import { MemosCard } from "@/components/MemosCard";
import { PayrollCard } from "@/components/PayrollCard";
import { ReceiptCapture } from "@/components/ReceiptCapture";
import { LogoutButton } from "@/components/LogoutButton";
import { SetupMenu } from "@/components/SetupMenu";
import { EntitySwitcher } from "@/components/EntitySwitcher";
import { HomeCustomize } from "@/components/HomeCustomize";
import { HomeSharedProvider, useHomeShared } from "@/components/tiles/HomeShared";
import { HomeTile } from "@/components/tiles/HomeTile";
import { useSelectedEntity } from "@/hooks/useSelectedEntity";
import { useHomeLayout } from "@/hooks/useHomeLayout";
import { DEFAULT_TILE_IDS } from "@/lib/tileIds";
import { tileById } from "@/lib/tileRegistry";
import "./Dashboard.css";

/*
 * ef-061-4 — Home is a TILE SHELL. It renders the signed-in login's saved tiles, in order, from the server
 * (`useHomeLayout()`), looking each id up in `lib/tileRegistry.ts`; the cards that used to be inlined here
 * (Cash on hand / Net farm income, Next payroll, the uncategorized-receipts insight, Recent activity) are the
 * registry components in `components/tiles/`. The live state they share (the SSE feed, the ledger delta, the
 * anomaly history, the payroll roster) is read ONCE by `HomeSharedProvider`, mounted below whatever the layout,
 * so the live-wall beats work with any subset of tiles on screen.
 *
 * NOT tiles, rendered whatever the layout: the horizon header, the scan, the cash-flow planner, the memos, the
 * cross-tenant warning, the cash-flow chart, the payroll card and the concept footer.
 */
interface DashboardProps {
  onNavigate: (path: string) => void;
  /** feature/auth. Dashboard renders its own masthead instead of AppShell's
   *  shared topbar (see AppShell.tsx's remarks on why), so it needs its own
   *  Log out control rather than inheriting one. */
  onLogout: () => Promise<void>;
}

export function Dashboard(props: DashboardProps) {
  return (
    <HomeSharedProvider>
      <DashboardView {...props} />
    </HomeSharedProvider>
  );
}

function DashboardView({ onNavigate, onLogout }: DashboardProps) {
  const {
    feedTransport, entityId, vendorById, accountById, employees,
    isLoading: dataLoading, hasError, crossTenantIds,
  } = useHomeShared();
  const layout = useHomeLayout();
  // ef-047-1 — Home's own read of the shared switcher, for the horizon row's name below.
  const { entities: switchableEntities, entityId: selectedEntityId } = useSelectedEntity();
  const selectedEntityName =
    switchableEntities.find((entity) => entity.id === selectedEntityId)?.name ?? null;

  // The layout counts toward `loading`: no tile is shown until it arrives. A failed READ is not an error state
  // of the screen — Home shows the standard set and says so (never a blank Home).
  const isLoading = dataLoading || layout.status === "loading";
  const layoutFailed = layout.status === "error";
  const tileIds = layoutFailed ? [...DEFAULT_TILE_IDS] : layout.tiles.map((t) => t.id);
  // An id with no registry component is skipped.
  const tiles = tileIds.flatMap((id) => {
    const Component = tileById(id)?.component;
    return Component ? [{ id, Component }] : [];
  });

  return (
    <div className="dashboard">
      <header className="dashboard__header">
        <Logo size={44} showTagline onDark />
        {/*
          THIS PILL USED TO BE A DECORATION. It was static JSX reading "Live" with a CSS
          pulse on the dot, so it said Live whether the stream was open, had dropped, or
          had never connected — a status indicator incapable of reporting bad status,
          which is the defect class this project has been hunting all week. The feed hook
          knew the answer the whole time and was never asked.

          It now renders useEntries().feedTransport. "Degraded" is not an error and is not
          styled as one: the entries still arrive, three seconds behind, which is the
          fallback working exactly as designed — the point is that the screen says so
          rather than claiming a push it is not getting.
        */}
        <span className={`dashboard__tenant-pill dashboard__tenant-pill--${feedTransport}`}>
          <span className="dashboard__live-dot" aria-hidden="true" />
          {feedTransport === "live"
            ? "Live"
            : feedTransport === "signed-out"
              ? "Signed out — reload to sign in"
              : feedTransport === "degraded"
                ? "Reconnecting"
                : "Connecting"}
        </span>
        {/*
          ef-037c (c5, screen-walked 2026-09-20): this used to be AppShell's own thin
          strip ABOVE this header — two bands stacked on a phone for one screen. AppShell
          renders nothing for the dashboard route now (AppShell.tsx), so the gear lives
          here instead, INSIDE the one horizon band. Never the active tab — Dashboard is
          never `/setup`. Hidden above 860px in CSS: the laptop sidebar already carries
          its own Setup control there (shell.spec.ts's AC1), so this copy would duplicate
          it rather than replace it.
        */}
        {/* ef-047-1 — the same switcher AppShell.tsx mounts for Fields/Grain/Livestock,
            so Home shows (and can change) the selected entity too. `dark` variant: this
            header sits on the horizon's deep-green gradient, not a white surface. */}
        <EntitySwitcher variant="dark" />
        <SetupMenu active={false} onNavigate={onNavigate} variant="topbar" />
        <LogoutButton onLogout={onLogout} className="dashboard__logout" />
      </header>

      {/*
        data-state is the tester's ground truth for this screen (rules/ui-breadcrumbs §1),
        and until now the home screen — the one the app opens on — did not expose one at
        all. A tester therefore had to infer "the dashboard has loaded" from the presence
        of KPI prose, which is why navigation.spec.ts waits for the words "cash on hand".

        That inference is measurably unreliable. Under five parallel Playwright workers
        against one dev server the KPI text can take longer than the 5s expect timeout, so
        that test fails roughly one run in five and passes on retry — reported as
        flakiness, when what it actually means is "the screen was still loading". Measured:
        --workers=1 gives 11/11, --workers=5 gives 1 failed in 10.

        Exposing the state does not fix that test — it is not mine to rewrite — but it
        gives it, and anything written next, something true to wait on instead of prose.
      */}
      <main className="dashboard__content" data-state={hasError ? "error" : isLoading ? "loading" : "ready"}>
        {/*
          A visually-hidden h1. This screen deliberately leads with the brand masthead
          rather than a page title — that is the design (ADR 0004's "one bold move"), and
          putting a visible "Dashboard" heading above it would undo it. But the page had
          NO h1 at all, which leaves a screen-reader user without the one heading that
          says what they have landed on, and leaves the heading outline starting at h2.
          Hidden visually, present in the accessibility tree, which is exactly what
          .visually-hidden is for.
        */}
        <h1 className="visually-hidden">
          Dashboard{selectedEntityName ? ` — ${selectedEntityName}` : ""}
        </h1>
        {/*
        {/*
          THE "+ Quick entry" BUTTON IS GONE, and it is not coming back as a rename.

          It navigated to /capture, a screen whose own header read "Capture expense" — the
          label promised one thing and the destination said another, so the only way to
          learn what it did was to press it. "Quick entry" is also already the name of
          /entry, the visitor's phone flow, so a single label pointed at two different
          destinations.

          The scan it was a shortcut TO now sits on this screen (see the side column), so
          there is nothing left to shortcut to. Reported by Deven, 2026-09-02.
        */}

        {/* Customize Home — ef-061-5's picker; reads and writes the same shared layout store. */}
        <HomeCustomize />

        {crossTenantIds > 0 && (
          /*
            THE SINGULAR CASE IS NOT HYPOTHETICAL, and the first draft got it wrong.
            This read "1 of these entries BELONG to a different farm" the first time it fired
            for real — one visitor entry against the wrong bundle, caught in a booth-loop run
            on 2026-09-07 rather than in review. The one-entry case is in fact the LIKELIEST
            way an operator meets this message: it appears the moment the first entry of the
            morning lands on a screen signed in to the other farm.
          */
          <p className="dashboard__cross-tenant" role="status" data-state="cross-tenant">
            <strong>This is the wrong farm for this screen.</strong>{" "}
            {crossTenantIds === 1
              ? "One entry here belongs to a different farm than the one this screen is set up "
                + "for, so its vendor and category cannot be shown."
              : `${crossTenantIds} entries here belong to a different farm than the one this `
                + "screen is set up for, so their vendors and categories cannot be shown."}{" "}
            Log out and sign in again on this address — nothing is wrong with the data.
          </p>
        )}
        <div className="dashboard__columns">
          <div className="dashboard__tiles">
            {layoutFailed && (
              <p className="dashboard__layout-note" role="status">
                Couldn't load your layout - showing the standard Home.
              </p>
            )}
            {layout.status === "ready" && tiles.length === 0 && (
              <StateMessage tone="empty" title="No tiles on Home." />
            )}
            {layout.status !== "loading" &&
              tiles.map(({ id, Component }) => (
                <HomeTile key={id} id={id}>
                  <Component />
                </HomeTile>
              ))}
          </div>

          <div className="dashboard__side">
            {/*
              First in the column, beside the feed on purpose. The booth motion is scan a
              receipt and watch it appear — splitting those across two screens (/capture
              had its own copy of the feed) broke the one gesture the demo is built on.
              vendorById and entityId are passed rather than refetched: the shared Home state
              already holds the reference data, and a second useReferenceData here would be a
              second copy that can disagree with the feed.
            */}
            <ReceiptCapture
              entityId={entityId}
              vendorById={vendorById}
              accountById={accountById}
              onNavigate={onNavigate}
            />
            {/* ef-048-5 — the read-only cash-flow projection. Its own data (useCashFlowPlanner). */}
            <CashFlowPlannerCard />
            {/* ef-051-1 — Home's own short running notes. */}
            <MemosCard />
            <CashFlowChart />
            <PayrollCard
              onReviewRun={() => onNavigate("/payroll")}
              employees={employees}
            />
          </div>
        </div>
      </main>

      {/*
        The one in-app link to the multi-entity roll-up CONCEPT screen
        (roadmap FR-2). Deliberately quiet — muted colour, small type, last
        thing on the page — because this is Christy's own path to a screen
        she navigates to on purpose while talking, not a call to action for
        a visitor. A phone visitor never sees it: the QR code lands them on
        /entry directly, never on this page. See App.tsx for the route.
      */}
      <footer className="dashboard__footer">
        <a className="dashboard__concept-link" href="/concept/rollup">
          Concept: multi-entity roll-up (FR-2)
        </a>
      </footer>
    </div>
  );
}
